// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { GroupCallLocator, TeamsMeetingLinkLocator } from '@azure/communication-calling';
import {
  AzureCommunicationTokenCredential,
  CommunicationTokenCredential,
  CommunicationUserIdentifier,
  createIdentifierFromRawId,
  isMicrosoftTeamsUserIdentifier,
  MicrosoftTeamsUserIdentifier
} from '@azure/communication-common';
import {
  CallAdapter,
  CallAndChatLocator,
  CallParticipantsLocator,
  CallWithChatAdapter,
  createAzureCommunicationCallAdapterFromClient,
  createAzureCommunicationCallWithChatAdapter,
  createStatefulCallClient,
  onResolveVideoEffectDependencyLazy,
  onResolveDeepNoiseSuppressionDependencyLazy,
  VideoBackgroundImage,
  TeamsCallAdapter,
  createTeamsCallAdapter,
  AzureCommunicationCallAdapterOptions,
  Profile,
  CommonCallAdapterOptions
} from '@azure/communication-react';
import { callAdapterLogger, chatAdapterLogger } from 'error-telemetry/azureAdapterLogger';
import { Constants } from '../constants';
import { JoinCallIdentityKind } from 'components/JoinCallButton';
import { authObject } from 'authentication/authConfig';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { authModule } from 'authentication/authModule';
import { fetchProfileDisplayNameWithCaching } from './GraphApiServices';

export const fetchCredentialsForACSIdentity = async (
  functionsAppToken: string
): Promise<{
  userId: { communicationUserId: string };
  tokenCredential: CommunicationTokenCredential;
  endpointUrl: string;
}> => {
  const userTokenResponse = await fetch('api/token', {
    headers: new Headers({
      Authorization: `Bearer ${functionsAppToken}`
    })
  });

  if (!userTokenResponse.ok) {
    throw new Error(`An error has occurred when fetching the user token with ${userTokenResponse.status} status code!`);
  }

  const userTokenResponseJson = await userTokenResponse.json();
  const userId = {
    communicationUserId: userTokenResponseJson.userAndToken.user.communicationUserId
  };
  const credential = new AzureCommunicationTokenCredential(userTokenResponseJson.userAndToken.token);
  const endpointUrl = userTokenResponseJson.endpointUrl;

  return {
    userId,
    tokenCredential: credential,
    endpointUrl
  };
};

interface TeamsTokenMSALData {
  instance: IPublicClientApplication;
  accounts: AccountInfo[];
}

const fetchCredentialsForTeamsIdentity = async (
  functionsAppToken: string,
  teamsTokenMSALData: TeamsTokenMSALData
): Promise<{
  userId: { microsoftTeamsUserId: string };
  tokenCredential: CommunicationTokenCredential;
  endpointUrl: string;
}> => {
  const teamsEndpointAppToken = await authModule.getTeamsEndpointToken(
    teamsTokenMSALData.instance,
    teamsTokenMSALData.accounts
  );

  const rawUserId = teamsTokenMSALData.accounts[0]?.localAccountId;

  if (!rawUserId) {
    throw new Error('User account ID is not available in the Teams token MSAL data');
  }

  console.log('teamsTokenMSALData:', teamsTokenMSALData);

  const teamsTokenResponse = await fetch('api/teamsToken', {
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${functionsAppToken}`
    }),
    body: JSON.stringify({
      teamsUserAadToken: teamsEndpointAppToken,
      clientId: authObject.clientId,
      userObjectId: rawUserId
    })
  });

  if (!teamsTokenResponse.ok) {
    throw new Error(
      `Error ${teamsTokenResponse.status} while fetching the Teams token: ${teamsTokenResponse.statusText}`
    );
  }

  interface TeamsTokenResponseBody {
    token: string;
    expiresOn: Date;
    endpointUrl: string;
  }
  const teamsTokenResponseJson: TeamsTokenResponseBody = await teamsTokenResponse.json();

  const userId = createIdentifierFromRawId(`8:orgid:${rawUserId}`);
  if (!isMicrosoftTeamsUserIdentifier(userId)) {
    throw new Error(`User ID is not a valid Microsoft Teams User ID ${rawUserId}`);
  }

  return {
    userId,
    tokenCredential: new AzureCommunicationTokenCredential(teamsTokenResponseJson.token),
    endpointUrl: teamsTokenResponseJson.endpointUrl
  };
};

const fetchACSCredentials = async (
  joinIdentity: JoinCallIdentityKind,
  functionsAppToken: string,
  teamsTokenMSALData: TeamsTokenMSALData
): Promise<{
  userId: CommunicationUserIdentifier | MicrosoftTeamsUserIdentifier;
  tokenCredential: CommunicationTokenCredential;
  endpointUrl: string;
}> => {
  if (joinIdentity === 'teams') {
    return fetchCredentialsForTeamsIdentity(functionsAppToken, teamsTokenMSALData);
  } else {
    return fetchCredentialsForACSIdentity(functionsAppToken);
  }
};

const onFetchProfile = async (token: string, userId: string, defaultProfile?: Profile): Promise<Profile> => {
  if (defaultProfile?.displayName) {
    return defaultProfile;
  }

  let displayName = token && (await fetchProfileDisplayNameWithCaching(userId, { token }));
  displayName ??= 'Unknown Participant';
  return {
    displayName
  };
};

export const constructPSTNCall = async (
  callLocator: CallParticipantsLocator,
  userName: string,
  functionsAppToken: string,
  alternateCallerId: string,
  joinIdentity: JoinCallIdentityKind,
  teamsTokenMSALData: TeamsTokenMSALData
): Promise<{
  callAdapter: CallAdapter | TeamsCallAdapter;
}> => {
  const displayName = userName;
  const { userId, tokenCredential } = await fetchACSCredentials(joinIdentity, functionsAppToken, teamsTokenMSALData);

  const callClient = createStatefulCallClient({ userId });
  const callAgent = await callClient.createCallAgent(tokenCredential, { displayName });

  const adapterOptions: AzureCommunicationCallAdapterOptions = {
    deepNoiseSuppressionOptions: {
      onResolveDependency: onResolveDeepNoiseSuppressionDependencyLazy
    },
    callingSounds: {
      callBusy: { url: '/audios/callBusy.mp3' },
      callRinging: { url: '/audios/callRinging.mp3' },
      callEnded: { url: '/audios/callEnded.mp3' }
    },
    alternateCallerId,
    onFetchProfile: (userId, defaultProfile) => onFetchProfile(functionsAppToken, userId, defaultProfile)
  };

  const callAdapter = isMicrosoftTeamsUserIdentifier(userId)
    ? await createTeamsCallAdapter({
        userId,
        credential: tokenCredential,
        locator: callLocator,
        options: adapterOptions
      })
    : await createAzureCommunicationCallAdapterFromClient(callClient, callAgent, callLocator, adapterOptions);

  callAdapter.on('error', (error) => {
    callAdapterLogger.error(JSON.stringify(error));
  });

  callAdapter.on('callIdChanged', () => {
    callAdapterLogger.verbose(
      JSON.stringify({ callId: callAdapter.getState().call?.id, message: 'CallId has been changed' })
    );
  });

  return {
    callAdapter
  };
};

export const joinMeeting = async (
  locator: CallAndChatLocator | TeamsMeetingLinkLocator,
  userName: string,
  functionsAppToken: string,
  videoBackgroundImages: VideoBackgroundImage[],
  joinIdentity: JoinCallIdentityKind,
  teamsTokenMSALData: TeamsTokenMSALData
): Promise<{
  adapter: CallWithChatAdapter | TeamsCallAdapter;
}> => {
  const displayName = `${userName}`;
  const { userId, tokenCredential, endpointUrl } = await fetchACSCredentials(
    joinIdentity,
    functionsAppToken,
    teamsTokenMSALData
  );

  if (!isTeamsMeetingLinkLocator(locator) && 'communicationUserId' in userId) {
    const addUserToChatUrl = `api/addUserToChat?communicationUserId=${userId.communicationUserId}&displayName=${displayName}&chatThreadId=${locator.chatThreadId}`;

    const addUserToChatUrlResponse = await fetch(addUserToChatUrl, {
      headers: new Headers({
        Authorization: `Bearer ${functionsAppToken}`
      })
    });

    if (!addUserToChatUrlResponse.ok) {
      throw new Error(`An error has occurred when adding a user with ${addUserToChatUrlResponse.status} status code!`);
    }
  }

  const callAdapterOptions: CommonCallAdapterOptions = {
    videoBackgroundOptions: {
      videoBackgroundImages,
      onResolveDependency: onResolveVideoEffectDependencyLazy
    },
    deepNoiseSuppressionOptions: {
      onResolveDependency: onResolveDeepNoiseSuppressionDependencyLazy
    },
    callingSounds: {
      callBusy: { url: '/audios/callBusy.mp3' },
      callRinging: { url: '/audios/callRinging.mp3' },
      callEnded: { url: '/audios/callEnded.mp3' }
    },
    reactionResources: {
      likeReaction: { url: '/assets/reactions/likeEmoji.png', frameCount: 102 },
      heartReaction: { url: '/assets/reactions/heartEmoji.png', frameCount: 102 },
      laughReaction: { url: '/assets/reactions/laughEmoji.png', frameCount: 102 },
      applauseReaction: { url: '/assets/reactions/clapEmoji.png', frameCount: 102 },
      surprisedReaction: { url: '/assets/reactions/surprisedEmoji.png', frameCount: 102 }
    },
    onFetchProfile: (userId, defaultProfile) => onFetchProfile(functionsAppToken, userId, defaultProfile)
  };

  let adapter: CallWithChatAdapter | TeamsCallAdapter;

  if ('microsoftTeamsUserId' in userId && 'meetingLink' in locator) {
    adapter = await createTeamsCallAdapter({
      userId,
      credential: tokenCredential,
      locator: locator,
      options: callAdapterOptions
    });

    adapter.on('error', (error) => {
      callAdapterLogger.error(JSON.stringify(error));
    });
  } else if ('communicationUserId' in userId) {
    adapter = await createAzureCommunicationCallWithChatAdapter({
      userId,
      displayName,
      credential: tokenCredential,
      endpoint: endpointUrl,
      locator,
      callAdapterOptions
    });

    adapter.on('callError', (error) => {
      callAdapterLogger.error(JSON.stringify(error));
    });

    adapter.on('chatError', (error) => {
      chatAdapterLogger.error(JSON.stringify(error));
    });
  } else {
    throw new Error('Unsupported join information. User identity type does not match call join information.');
  }

  adapter.on('callIdChanged', () => {
    callAdapterLogger.verbose(
      JSON.stringify({ callId: adapter.getState().call?.id, message: 'CallId has been changed' })
    );
  });

  pushMeetingToBrowserURL(locator);
  return {
    adapter
  };
};

export const isTeamsMeetingLinkLocator = (
  locator: CallAndChatLocator | TeamsMeetingLinkLocator
): locator is TeamsMeetingLinkLocator => {
  return 'meetingLink' in locator;
};

const pushMeetingToBrowserURL = (locator: CallAndChatLocator | TeamsMeetingLinkLocator): void => {
  const url = new URL(window.location.href);

  if (isTeamsMeetingLinkLocator(locator)) {
    url.searchParams.set(Constants.TEAMS_MEETING_LINK_INDICATOR, locator.meetingLink);
  } else {
    url.searchParams.set(Constants.ACS_CALL_PARAM_NAME, (locator.callLocator as GroupCallLocator).groupId);
    url.searchParams.set(Constants.ACS_CHAT_PARAM_NAME, locator.chatThreadId);
  }

  window.history.pushState({}, document.title, url.toString());
};
